import { ImageFragment, ImageOverrideFragment } from '.generated/dato'
import { cx } from 'lib/cx'
import NextImage, { ImageProps } from 'next/image'
import { preload } from 'react-dom'

export type DatoImageProps = Omit<
  ImageProps,
  'src' | 'width' | 'height' | 'alt' | 'unoptimized' | 'blurDataURL'
> & {
  file: ImageFragment | null | undefined
  scale?: number
}

export default function DatoImage({
  file,
  scale,
  placeholder,
  ...nextImageProps
}: DatoImageProps) {
  if (!file) {
    return null
  }

  if (!file.width || !file.height) {
    return <NextImage unoptimized src={file.url} alt={file.alt ?? undefined} />
  }

  const { fill } = nextImageProps

  return (
    <NextImage
      src={file.url}
      width={fill ? undefined : file.width * (scale ?? 1)}
      height={fill ? undefined : file.height * (scale ?? 1)}
      alt={file.alt ?? undefined}
      blurDataURL={file.blurUpThumb ?? undefined}
      placeholder={file.blurUpThumb ? placeholder : 'empty'}
      {...nextImageProps}
    />
  )
}

export function DatoBackgroundImage(props: {
  file: ImageFragment | null
  className?: string
  opacity?: number
  maskImage?: string
}) {
  return (
    <div className={cx(props.className, 'absolute inset-0 -z-10')}>
      <div
        style={{ opacity: props.opacity, maskImage: props.maskImage }}
        className="relative w-full h-full"
      >
        <DatoImage
          file={props.file}
          fill
          style={{ objectFit: 'cover' }}
          placeholder="blur"
        />
      </div>
    </div>
  )
}

/**
 * Candidate widths offered in a srcset. The browser picks the smallest
 * candidate that still covers `sizes` × device pixel ratio, so fine grained
 * steps keep the overshoot – and with it the wasted bytes – small.
 */
const srcSetWidths = [
  320, 400, 480, 560, 640, 720, 800, 900, 1000, 1120, 1280, 1440, 1600, 1800,
  2000, 2240, 2560,
]

type DatoResponsiveGraphicProps = {
  mobile: ImageFragment
  desktop: ImageOverrideFragment | null
  className?: string
  priority?: boolean
  sizes?: string
}

/**
 * Used to show different graphics on different viewports.
 * SVGs are served as uploaded, raster assets get a resized srcset so an
 * accidental photo upload does not ship its full resolution to the browser.
 */
export function DatoResponsiveGraphic({
  desktop,
  mobile,
  className,
  priority,
  sizes = '100vw',
}: DatoResponsiveGraphicProps) {
  const mobileCandidates = getGraphicCandidates(mobile)
  const desktopCandidates = desktop ? getGraphicCandidates(desktop) : undefined

  if (priority) {
    preload(mobileCandidates?.src ?? mobile.url, {
      as: 'image',
      fetchPriority: 'high',
      media: '(max-width: 767px)',
      imageSrcSet: mobileCandidates?.srcSet,
      imageSizes: mobileCandidates && sizes,
    })
    if (desktop) {
      preload(desktopCandidates?.src ?? desktop.url, {
        as: 'image',
        fetchPriority: 'high',
        media: '(min-width: 768px)',
        imageSrcSet: desktopCandidates?.srcSet,
        imageSizes: desktopCandidates && sizes,
      })
    }
  }

  return (
    <picture>
      {desktop && (
        <source
          media="(min-width: 768px)"
          width={desktop.width!}
          height={desktop.height!}
          className="hidden"
          srcSet={desktopCandidates?.srcSet ?? desktop.url}
          sizes={desktopCandidates && sizes}
        />
      )}
      <img
        src={mobileCandidates?.src ?? mobile.url}
        srcSet={mobileCandidates?.srcSet}
        sizes={mobileCandidates && sizes}
        width={mobile.width!}
        height={mobile.height!}
        alt={mobile.alt}
        className={cx('max-w-full', className)}
        fetchPriority={priority ? 'high' : 'auto'}
      />
    </picture>
  )
}

/**
 * Resizing an SVG through imgix rasterizes it, so vector uploads have to stay
 * untouched. Returns undefined for them and for assets of unknown size.
 */
function getGraphicCandidates(
  file: ImageFragment | ImageOverrideFragment,
): { src: string; srcSet: string } | undefined {
  const intrinsicWidth = file.width

  if (!intrinsicWidth || /\.svg(\?|$)/i.test(file.url)) {
    return undefined
  }

  const candidates = srcSetWidths.filter((width) => width <= intrinsicWidth)
  const widest = candidates.at(-1)

  if (!widest) {
    return undefined
  }

  function resized(width: number) {
    const url = new URL(file.url)
    url.searchParams.set('w', width.toString())
    url.searchParams.set('fm', 'webp')
    return url.toString()
  }

  return {
    src: resized(widest),
    srcSet: candidates.map((width) => `${resized(width)} ${width}w`).join(', '),
  }
}

export function DatoResponsiveCroppedImage({
  image,
  baseWidth,
  baseHeight,
  baseSizes,
  breakpoints,
  className,
  priority,
}: {
  image: ImageFragment
  baseWidth: number
  baseHeight: number
  /**
   * Layout width of the image below the smallest breakpoint. Pass a viewport
   * relative value whenever the image is fluid there, otherwise phones request
   * an image sized for the largest viewport the base crop is used on.
   */
  baseSizes?: string
  breakpoints: {
    screen: number
    width: number
    height: number
  }[]
  className?: string
  priority?: boolean
}) {
  const imageUrl = new URL(image.url)

  function getCroppedUrl(width: number, height: number) {
    const url = new URL(imageUrl)
    url.searchParams.set('w', width.toString())
    url.searchParams.set('h', height.toString())
    url.searchParams.set('fm', 'webp')
    url.searchParams.set('fit', 'crop')
    return url.toString()
  }

  function getSrcSet(width: number, height: number) {
    const maxWidth = width * 3
    const fittingWidths = srcSetWidths.filter(
      (candidate) => candidate <= maxWidth,
    )
    const candidates = fittingWidths.length ? fittingWidths : [width]

    return candidates
      .map(
        (candidate) =>
          `${getCroppedUrl(candidate, Math.round((candidate * height) / width))} ${candidate}w`,
      )
      .join(', ')
  }

  return (
    <picture className={className}>
      {breakpoints
        .sort((a, b) => b.screen - a.screen)
        .map(({ screen, width, height }) => (
          <source
            key={screen}
            media={`(min-width: ${screen}px)`}
            srcSet={getSrcSet(width, height)}
            sizes={`${width}px`}
          />
        ))}
      <img
        src={getCroppedUrl(baseWidth, baseHeight)}
        srcSet={getSrcSet(baseWidth, baseHeight)}
        sizes={baseSizes ?? `${baseWidth}px`}
        width={baseWidth}
        height={baseHeight}
        alt={image.alt}
        className="h-full w-full object-cover"
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
      />
    </picture>
  )
}
